import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useState } from "react";
import { getUnexpiredToken, logAxiosError } from "@/utils/utils";
import { toast } from "sonner";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { useSession } from "@clerk/clerk-react";

interface Plan {
  key: string;
  name: string;
  price: number;
  description: string;
}

const plans: Plan[] = [
  {
    key: "new_pro",
    name: "Pro",
    price: 30,
    description: "150 credits",
  },
  {
    key: "new_scale",
    name: "Scale",
    price: 50,
    description: "250 credits",
  },
  {
    key: "new_ultimate",
    name: "Ultimate",
    price: 100,
    description: "🎉 555 credits 500 credits",
  },
  {
    key: "new_power",
    name: "Power",
    price: 350,
    description: "🎉 2150 credits 1750 credits",
  },
  {
    key: "new_unlimited",
    name: "Unlimited",
    price: 500,
    description: "unlimited credits",
  },
];

export function Plans() {
  const rawSubscriptionTier = usePlanInfoStore(
    (state) => state.subscriptionTier,
  );
  const subscriptionTier = rawSubscriptionTier || "free";
  const visiblePlans = plans.filter(
    (plan) =>
      plan.key !== "new_unlimited" || subscriptionTier === "new_unlimited",
  );

  const currentPlanIndex = visiblePlans.findIndex(
    (plan) => plan.key === subscriptionTier,
  );

  const { session } = useSession();

  const [loadingPlan, setLoadingPlan] = useState(null);

  const referral = usePlanInfoStore((state) => state.referral);

  const onClickPlan = async (plan?: Plan, promptPackage?: boolean) => {
    if (session) {
      const supabaseJWT = await getUnexpiredToken(session);

      // @ts-expect-error TS2345
      setLoadingPlan(promptPackage ? "prompt_package" : plan.key);

      try {
        const urlPath =
          rawSubscriptionTier && !promptPackage
            ? "create-customer-portal-session"
            : "create-checkout-session";

        const res = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/stripe/${urlPath}`,
          {
            success_url: window.location.href,
            cancel_url: window.location.href,
            return_url: window.location.href,
            subscription_tier: plan ? plan.key : null,
            prompt_package_id: promptPackage ? "250" : null,
            referral_id: referral,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        );

        if (res.data.url) {
          window.location.href = res.data.url;
        } else {
          toast.error("Failed to create stripe session");
        }
      } catch (error) {
        logAxiosError("Failed to create stripe session", error);
        toast.error("Failed to create stripe session");
      } finally {
        setLoadingPlan(null);
      }
    }
  };

  return (
    <div className="w-full relative">
      <div className="flex flex-col -space-y-2">
        {visiblePlans.map((plan, index) => (
          <div
            key={plan.name}
            className={`rounded-xl ${
              plan.key === subscriptionTier
                ? "border border-[#6F55F2] scale-[1.02] relative z-10 mb-4!"
                : "relative"
            } bg-linear-to-b from-[#1F1F22] to-[#131519] p-4 pb-6`}
          >
            <div className="flex flex-col gap-2">
              <Badge
                variant="outline"
                className="w-fit px-2 py-1 text-[10px] leading-3 text-[#9F9FA4] border-[#2F3035] rounded-[4px] bg-transparent"
              >
                {plan.name}
              </Badge>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="leading-[34px] text-[#F5F5F6] font-normal text-[26px]">
                    ${plan.price}
                  </span>
                  <span className="text-[14px] leading-[26px] text-[#9F9FA4]">
                    / month for{" "}
                    {plan.description.includes("🎉") ? (
                      <>
                        🎉{" "}
                        <span className="text-[#6F55F2]">
                          {plan.description.split(" ")[1]}{" "}
                          {plan.description.split(" ")[2]}
                        </span>{" "}
                        <span className="line-through text-[#F5F5F6]">
                          {plan.description.split(" ")[3]}{" "}
                          {plan.description.split(" ")[4]}
                        </span>
                      </>
                    ) : (
                      <span className="text-[#F5F5F6]">{plan.description}</span>
                    )}
                  </span>
                </div>
                <Button
                  variant={"outline"}
                  size="sm"
                  className={`text-xs leading-4 px-3 py-1.5 h-auto font-medium rounded-md border-solid! border border-[#2F3035] hover:border-[#2F3035] hover:bg-background-secondary`}
                  // @ts-expect-error TS2322
                  disabled={loadingPlan}
                  onClick={() => onClickPlan(plan)}
                >
                  {loadingPlan === plan.key ? (
                    <Loader2 className="size-4 animate-spin mr-1" />
                  ) : null}
                  <span>
                    {plan.key === subscriptionTier
                      ? "Manage plan"
                      : currentPlanIndex === -1 || index > currentPlanIndex
                        ? "Upgrade"
                        : "Downgrade"}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mx-auto bg-linear-to-b from-[#1F1F22] to-[#131519] p-4 mt-[-8px] relative z-20 rounded-xl border border-[#2F3035] max-w-[418px]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="leading-[34px] font-medium text-[#F5F5F6] text-xl">
              $50
            </span>
            <span className="text-[14px] leading-[26px] text-[#9F9FA4]">
              for <span className="text-[#F5F5F6]">250 bonus credits</span> (do
              not expire)
            </span>
          </div>
          <div>
            <div
              data-tooltip-id="tooltip"
              data-tooltip-place="top"
              data-tooltip-content={
                !subscriptionTier?.startsWith("new_")
                  ? "Subscribe to a paid plan to purchase bonus credits"
                  : null
              }
            >
              <Button
                size="sm"
                className="text-xs leading-4 px-3 py-1.5 h-auto font-medium rounded-md bg-[#6F55F2] text-white hover:bg-[#5B44D6]"
                disabled={!subscriptionTier?.startsWith("new_")}
                onClick={() => {
                  // @ts-expect-error TS2345
                  onClickPlan(null, true);
                }}
              >
                {loadingPlan === "prompt_package" ? (
                  <Loader2 className="size-4 animate-spin mr-1" />
                ) : null}
                <span>Get 250</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function getPlanNameFromKey(key: string | null) {
  if (!key || key === "free") {
    return "No active plan";
  }
  return (
    plans.find((plan) => plan.key === key)?.name ??
    key.charAt(0).toUpperCase() + key.slice(1) + " (old)"
  );
}
